import {
  createContext,
  useReducer,
  useContext,
  FC,
  useCallback,
  useEffect,
  ReactNode
} from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Appointments,
  Appointments_appointmentsForDoctor_data_results_dates as AppointmentDateType
} from 'gql/appointments/__generated__/Appointments';
import { GET_APPOINTMENTS } from 'gql/appointments/queries';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
  State,
  Dispatch,
  DispatchContext,
  CalendarViewType,
  ActiveEventType,
  CalendarState,
  CalendarEventState,
  UpdateCalendarEventType,
  AddCalendarEventType
} from './types';
import reducer from './reducers';
import {
  ActionTypes,
  CALENDAR_EVENT_REPEATING,
  CALENDAR_EVENT_STATUSES,
  CALENDAR_EVENT_TYPES,
  CALENDAR_VIEW
} from './constants';
import { GET_CALENDAR_EVENTS } from './graphql/queries';
import { CalendarEvents } from './graphql/__generated__/CalendarEvents';
import { EVENT_COLORS } from 'pages/Calendar/constants';
import {
  CREATE_CALENDAR_EVENT,
  DELETE_CALENDAR_EVENT,
  UPDATE_CALENDAR_EVENT
} from './graphql/mutations';
import { CreateCalendarEvent } from './graphql/__generated__/CreateCalendarEvent';
import { getStatusColor } from 'utils/appointments';
import { showErrorMessage } from 'utils/showErrorMessage';

const initialState: State = {
  view: CALENDAR_VIEW.week,
  events: [],
  appointments: [],
  activeEvent: null,
  dates: null
};

const CalendarStateContext = createContext<CalendarState | undefined>(
  undefined
);

const CalendarEventStateContext = createContext<CalendarEventState | undefined>(
  undefined
);

const CalendarDispatchContext = createContext<Dispatch | undefined>(undefined);

const CalendarProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // context
  const [{ view, events, appointments, activeEvent, dates }, dispatch] =
    useReducer(reducer, {
      ...initialState
    });

  // translations
  const { t } = useTranslation();

  // graphql
  const [getEvents] = useLazyQuery<CalendarEvents>(GET_CALENDAR_EVENTS, {
    onCompleted(data) {
      const calendarEvents = data?.calendarEvents?.data;

      if (calendarEvents) {
        const eventsData = calendarEvents?.map(item => {
          if (item.type === CALENDAR_EVENT_REPEATING.no_repeat) {
            return {
              ...item,
              id: String(item.id),
              title: item.title || '',
              color: item.color || EVENT_COLORS[0],
              extendedProps: {
                status:
                  item.extendedProps?.status || CALENDAR_EVENT_STATUSES.new,
                description: item.extendedProps?.description || '',
                repeat:
                  item.extendedProps?.repeat ||
                  CALENDAR_EVENT_REPEATING.no_repeat,
                type: CALENDAR_EVENT_TYPES.event
              }
            };
          }

          return {
            id: String(item.id),
            startTime: item.start,
            endTime: item.end,
            title: item.title || '',
            color: item.color || EVENT_COLORS[0],
            editable: !!item.extendedProps?.editable,
            startRecur: item.extendedProps?.startRecur,
            daysOfWeek: item.extendedProps?.daysOfWeek,
            extendedProps: {
              status: item.extendedProps?.status || CALENDAR_EVENT_STATUSES.new,
              description: item.extendedProps?.description || '',
              repeat:
                item.extendedProps?.repeat || CALENDAR_EVENT_REPEATING.daily,
              type: CALENDAR_EVENT_TYPES.event
            }
          };
        });

        dispatch({ type: ActionTypes.SET_EVENTS, data: eventsData });
      }
    }
  });

  const [getAppointments] = useLazyQuery<Appointments>(GET_APPOINTMENTS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted(data) {
      const appointments = data?.appointmentsForDoctor?.data.results;

      if (appointments) {
        const eventsData = appointments
          ?.filter(item => item.dates.some(date => date.accepted))
          .map(item => {
            const startDate = item.dates.find(
              date => date.accepted
            ) as AppointmentDateType;

            const duration = item.plan.duration || 1;

            const end = dayjs(startDate.date).add(duration, 'hours').format();

            const title = t('calendar.appointment_with', {
              name: `${item.patient.first_name} ${item.patient.last_name}`
            });

            return {
              id: String(item.id),
              start: startDate.date,
              end,
              title: title,
              color: getStatusColor(item.status),
              editable: false,
              extendedProps: {
                status: CALENDAR_EVENT_STATUSES.existing,
                repeat: CALENDAR_EVENT_REPEATING.no_repeat,
                type: CALENDAR_EVENT_TYPES.appointment
              }
            };
          });

        dispatch({ type: ActionTypes.SET_APPOINTMENTS, data: eventsData });
      }
    }
  });

  useEffect(() => {
    if (dates) {
      getEvents({
        variables: {
          query: dates
        }
      });
      // TODO: set dates
      getAppointments({
        variables: {
          query: {
            name: '',
            plan_type: '',
            pagination: {
              page: 0,
              limit: 200
            }
          }
        }
      });
    }
  }, [dates, getAppointments, getEvents]);

  return (
    <CalendarStateContext.Provider
      value={{ view, events, appointments, dates }}
    >
      <CalendarEventStateContext.Provider value={{ activeEvent }}>
        <CalendarDispatchContext.Provider value={dispatch}>
          {children}
        </CalendarDispatchContext.Provider>
      </CalendarEventStateContext.Provider>
    </CalendarStateContext.Provider>
  );
};

const useCalendarStateContext = (): CalendarState => {
  const context = useContext(CalendarStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useCalendarStateContext must be used within a useCalendarStateContext'
    );
  }

  return context;
};

const useCalendarEventStateContext = (): CalendarEventState => {
  const context = useContext(CalendarEventStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useCalendarStateContext must be used within a useCalendarStateContext'
    );
  }

  return context;
};

const useCalendarDispatchContext = (): DispatchContext => {
  const dispatch = useContext(CalendarDispatchContext);
  // graphql
  const [createCalendarEvent] = useMutation<CreateCalendarEvent>(
    CREATE_CALENDAR_EVENT
  );

  const [updateCalendarEvent] = useMutation(UPDATE_CALENDAR_EVENT, {
    onError: err => showErrorMessage(err)
  });

  const [deleteCalendarEvent] = useMutation(DELETE_CALENDAR_EVENT, {
    onError: err => showErrorMessage(err)
  });

  if (typeof dispatch === 'undefined') {
    throw new Error(
      'useCalendarDispatchContext must be used within a useCalendarDispatchContext'
    );
  }

  const setView = useCallback(
    (value: CalendarViewType) => {
      dispatch({ type: ActionTypes.SET_VIEW, data: value });
    },
    [dispatch]
  );

  const addEvent = useCallback(
    async (event: AddCalendarEventType) => {
      const data = await createCalendarEvent({
        variables: {
          input: {
            start: event.event.start || event.event.startTime,
            end: event.event.end || event.event.endTime,
            color: event.event.color || EVENT_COLORS[0],
            type:
              event.event.extendedProps?.repeat ||
              CALENDAR_EVENT_REPEATING.no_repeat,
            extendedProps: {
              ...event.event.extendedProps,
              type: CALENDAR_EVENT_TYPES.event,
              editable: event.event.editable,
              startRecur: event.event.startRecur,
              daysOfWeek: event.event.daysOfWeek
            }
          }
        }
      });

      const id = data.data?.createCalendarEvent?.data.id;

      if (id) {
        dispatch({
          type: ActionTypes.ADD_EVENT,
          data: {
            event: {
              ...event.event,
              id: String(id)
            },
            ui: {
              ...event.ui,
              id: String(id)
            }
          }
        });
      }
    },
    [createCalendarEvent, dispatch]
  );

  const updateEvent = useCallback(
    (event: UpdateCalendarEventType) => {
      updateCalendarEvent({
        variables: {
          id: Number(event.id),
          input: {
            title: event.title,
            start: event.start || event.startTime,
            end: event.end || event.endTime,
            color: event.color || EVENT_COLORS[0],
            type: event.extendedProps?.repeat,
            extendedProps: {
              ...event.extendedProps,
              type: CALENDAR_EVENT_TYPES.event,
              editable: event.editable,
              startRecur: event.startRecur,
              daysOfWeek: event.daysOfWeek
            }
          }
        }
      });
      dispatch({ type: ActionTypes.UPDATE_EVENT, data: event });
    },
    [dispatch, updateCalendarEvent]
  );

  const setActiveEvent = useCallback(
    (event: ActiveEventType | null) => {
      dispatch({ type: ActionTypes.SET_ACTIVE_EVENT, data: event });
    },
    [dispatch]
  );

  const deleteEvent = useCallback(
    (id: string) => {
      deleteCalendarEvent({ variables: { id: Number(id) } });
      dispatch({ type: ActionTypes.DELETE_EVENT, data: id });
    },
    [deleteCalendarEvent, dispatch]
  );

  const setDates = useCallback(
    (start: Date, end: Date) => {
      dispatch({ type: ActionTypes.SET_DATES, data: { start, end } });
    },
    [dispatch]
  );

  return {
    setView,
    addEvent,
    updateEvent,
    setActiveEvent,
    deleteEvent,
    setDates
  };
};

export default CalendarProvider;
export {
  useCalendarDispatchContext,
  useCalendarStateContext,
  useCalendarEventStateContext
};
